import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  Award,
  Scissors,
  Eye,
  Heart,
  Brain,
  Activity,
  Stethoscope,
  Users,
  Star,
  ArrowRight,
  Sparkles,
} from 'lucide-react';

const ShalyaTantra = () => {
  const [selectedBranch, setSelectedBranch] = useState('all');

  const branches = [
    {
      id: 'general',
      name: 'General Surgery',
      icon: Scissors,
      description: 'Core surgical procedures including wound management, abscess drainage, and general operative techniques',
      procedures: ['Wound Management', 'Abscess Drainage', 'Fistula Treatment', 'Tumor Excision'],
      accent: 'cyan',
    },
    {
      id: 'ophthalmology',
      name: 'Netra Tantra (Ophthalmology)',
      icon: Eye,
      description: 'Eye diseases and surgical interventions including cataract surgery and pterygium removal',
      procedures: ['Cataract Surgery', 'Pterygium Removal', 'Eyelid Disorders', 'Conjunctival Surgery'],
      accent: 'green',
    },
    {
      id: 'ent',
      name: 'Shalakya Tantra (ENT)',
      icon: Activity,
      description: 'Ear, nose, throat, and head-neck surgical procedures',
      procedures: ['Rhinoplasty', 'Tonsillectomy', 'Nasal Polyp Removal', 'Ear Surgery'],
      accent: 'violet',
    },
    {
      id: 'orthopedics',
      name: 'Asthi & Sandhi Chikitsa',
      icon: Heart,
      description: 'Bone and joint disorders, fracture management, and orthopedic interventions',
      procedures: ['Fracture Management', 'Dislocation Treatment', 'Joint Disorders', 'Bone Healing'],
      accent: 'pink',
    },
    {
      id: 'proctology',
      name: 'Anorectal Surgery',
      icon: Stethoscope,
      description: 'Kshara Sutra therapy and management of anorectal disorders',
      procedures: ['Fistula-in-Ano', 'Hemorrhoids', 'Fissure Treatment', 'Pilonidal Sinus'],
      accent: 'purple',
    },
    {
      id: 'plastic',
      name: 'Plastic & Reconstructive',
      icon: Brain,
      description: 'Cosmetic and reconstructive surgical procedures',
      procedures: ['Rhinoplasty', 'Ear Lobe Repair', 'Lip Reconstruction', 'Scar Management'],
      accent: 'orange',
    },
  ];

  const eightProcedures = [
    {
      name: 'Chedana',
      sanskrit: 'छेदन',
      meaning: 'Excision',
      description: 'Cutting or excision of diseased tissue, tumors, or abnormal growths',
      applications: 'Tumor removal, abscess drainage, tissue excision',
    },
    {
      name: 'Bhedana',
      sanskrit: 'भेदन',
      meaning: 'Incision',
      description: 'Making incisions to access deeper structures or drain collections',
      applications: 'Abscess incision, surgical access, drainage procedures',
    },
    {
      name: 'Lekhana',
      sanskrit: 'लेखन',
      meaning: 'Scraping',
      description: 'Scraping or curettage of unhealthy tissue or bone',
      applications: 'Wound debridement, bone scraping, tissue cleaning',
    },
    {
      name: 'Vedhana',
      sanskrit: 'वेधन',
      meaning: 'Puncturing',
      description: 'Puncturing or piercing for drainage or therapeutic purposes',
      applications: 'Fluid aspiration, therapeutic puncture, drainage',
    },
    {
      name: 'Eshana',
      sanskrit: 'एषण',
      meaning: 'Probing',
      description: 'Probing to explore wounds, sinuses, or fistulous tracts',
      applications: 'Fistula probing, wound exploration, tract assessment',
    },
    {
      name: 'Aharya',
      sanskrit: 'आहार्य',
      meaning: 'Extraction',
      description: 'Extraction or removal of foreign bodies, stones, or embedded objects',
      applications: 'Foreign body removal, stone extraction, embedded object removal',
    },
    {
      name: 'Visravana',
      sanskrit: 'विस्रावण',
      meaning: 'Drainage',
      description: 'Facilitating drainage of pus, blood, or other fluids',
      applications: 'Abscess drainage, fluid evacuation, pus removal',
    },
    {
      name: 'Sivana',
      sanskrit: 'सीवन',
      meaning: 'Suturing',
      description: 'Suturing or stitching of wounds for healing',
      applications: 'Wound closure, tissue approximation, surgical repair',
    },
  ];

  const modernRelevance = [
    {
      title: 'Kshara Sutra Therapy',
      description: 'Ancient technique for fistula-in-ano treatment, now recognized globally as a minimally invasive alternative to surgery',
      impact: 'Used in over 50 countries with 85-95% success rate',
    },
    {
      title: 'Agnikarma (Therapeutic Cautery)',
      description: 'Controlled thermal therapy for pain management and tissue healing',
      impact: 'Modern applications in chronic pain and sports injuries',
    },
    {
      title: 'Jalaukavacharana (Leech Therapy)',
      description: 'Bloodletting therapy using medicinal leeches for various conditions',
      impact: 'FDA-approved for microsurgery and venous congestion',
    },
    {
      title: 'Raktamokshana (Bloodletting)',
      description: 'Controlled blood removal for specific therapeutic purposes',
      impact: 'Used in polycythemia and certain dermatological conditions',
    },
  ];

  const educationPath = [
    {
      level: 'Undergraduate',
      label: 'BAMS',
      duration: '5.5 years',
      focus: 'Foundation in Shalya Tantra principles, basic surgical skills, and clinical exposure',
      subjects: ['Shalya Tantra theory', 'Surgical anatomy', 'Pre & post-operative care', 'Basic procedures'],
    },
    {
      level: 'Postgraduate',
      label: 'MS Shalya Tantra',
      duration: '3 years',
      focus: 'Advanced surgical training, research, and specialization in specific areas',
      subjects: ['Advanced surgical techniques', 'Research methodology', 'Specialized procedures', 'Teaching skills'],
    },
    {
      level: 'Fellowship',
      label: 'Super-specialization',
      duration: '1-2 years',
      focus: 'Expertise in specific surgical domains like Kshara Sutra, Plastic Surgery, etc.',
      subjects: ['Specialized techniques', 'Advanced procedures', 'Clinical research', 'Innovation'],
    },
  ];

  const filteredBranches = selectedBranch === 'all'
    ? branches
    : branches.filter((b) => b.id === selectedBranch);

  return (
    <div className="shalya-page">
      {/* Hero */}
      <section className="shalya-hero">
        <div className="container">
          <div className="shalya-hero-content text-center">
            <span className="shalya-eyebrow">
              <Sparkles size={14} />
              One of the Eight Branches of Ayurveda
            </span>
            <h1>
              <span className="sanskrit">शल्य तन्त्र</span>
              <br />
              Shalya Tantra
            </h1>
            <p className="shalya-hero-subtitle">
              The Ancient Science of Surgery &middot; Ayurvedic Surgical Excellence &middot; Bridging Tradition and Innovation
            </p>
            <div className="shalya-hero-stats">
              <div className="shalya-stat">
                <span className="shalya-stat-number">300+</span>
                <span className="shalya-stat-label">Surgical Procedures</span>
              </div>
              <div className="shalya-stat">
                <span className="shalya-stat-number">120+</span>
                <span className="shalya-stat-label">Surgical Instruments</span>
              </div>
              <div className="shalya-stat">
                <span className="shalya-stat-number">2500+</span>
                <span className="shalya-stat-label">Years of Legacy</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="section">
        <div className="container">
          <div className="shalya-intro">
            <p className="lead">
              <strong>Shalya Tantra</strong> is one of the eight major branches of Ayurveda, dedicated to the
              science and art of surgery. The term &ldquo;Shalya&rdquo; refers to foreign bodies or surgical
              interventions, while &ldquo;Tantra&rdquo; means a systematic science or technique.
            </p>
            <p>
              This ancient surgical discipline, primarily documented in the <strong>Sushruta Samhita</strong>,
              encompasses a comprehensive understanding of surgical anatomy, operative techniques, pre and
              post-operative care, and the management of surgical conditions — one of the most advanced surgical
              systems of the ancient world.
            </p>
          </div>
        </div>
      </section>

      {/* Eight Fundamental Procedures */}
      <section className="section section-alt">
        <div className="container">
          <div className="content-header">
            <h2>Ashtavidha Shastra Karma</h2>
            <p>The eight fundamental surgical procedures that underpin every technique in Shalya Tantra</p>
          </div>
          <div className="shalya-procedures">
            {eightProcedures.map((procedure, index) => (
              <div key={index} className="shalya-procedure-row">
                <div className="shalya-procedure-sanskrit">
                  <span>{procedure.sanskrit}</span>
                </div>
                <div className="shalya-procedure-body">
                  <div className="shalya-procedure-title">
                    <h3>{procedure.name}</h3>
                    <span className="shalya-procedure-meaning">{procedure.meaning}</span>
                  </div>
                  <p className="shalya-procedure-description">{procedure.description}</p>
                  <div className="shalya-procedure-applications">
                    <Star size={14} />
                    <span>{procedure.applications}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Branches of Shalya Tantra */}
      <section className="section">
        <div className="container">
          <div className="content-header">
            <h2>Branches of Shalya Tantra</h2>
            <p>Specialized surgical domains in Ayurvedic practice</p>
          </div>

          <div className="branch-filters">
            <button
              type="button"
              className={`filter-btn ${selectedBranch === 'all' ? 'active' : ''}`}
              onClick={() => setSelectedBranch('all')}
            >
              All Branches
            </button>
            {branches.map((branch) => (
              <button
                key={branch.id}
                type="button"
                className={`filter-btn ${selectedBranch === branch.id ? 'active' : ''}`}
                onClick={() => setSelectedBranch(branch.id)}
              >
                {branch.name}
              </button>
            ))}
          </div>

          <div className="shalya-branches-grid">
            {filteredBranches.map((branch, index) => (
              <div key={index} className={`shalya-branch-card card shalya-accent-${branch.accent}`}>
                <div className="shalya-branch-icon">
                  <branch.icon size={28} />
                </div>
                <h3>{branch.name}</h3>
                <p className="branch-description">{branch.description}</p>
                <div className="branch-procedures">
                  <h4>Key Procedures</h4>
                  <ul>
                    {branch.procedures.map((proc, idx) => (
                      <li key={idx}>
                        <ArrowRight size={14} />
                        <span>{proc}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modern Relevance */}
      <section className="section section-alt">
        <div className="container">
          <div className="content-header">
            <h2>Modern Clinical Applications</h2>
            <p>Ancient techniques validated by contemporary research</p>
          </div>
          <div className="shalya-relevance-grid">
            {modernRelevance.map((item, index) => (
              <div key={index} className="shalya-relevance-card card">
                <Award size={22} className="shalya-relevance-icon" />
                <h3>{item.title}</h3>
                <p>{item.description}</p>
                <div className="impact-badge">
                  <Star size={14} />
                  <span>{item.impact}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Education Path */}
      <section className="section">
        <div className="container">
          <div className="content-header">
            <h2>Educational Pathway in Shalya Tantra</h2>
            <p>Journey to becoming an Ayurvedic surgeon</p>
          </div>
          <div className="shalya-education-path">
            {educationPath.map((level, index) => (
              <React.Fragment key={index}>
                <div className="shalya-education-step">
                  <div className="shalya-education-step-marker">{index + 1}</div>
                  <div className="shalya-education-step-content">
                    <div className="shalya-education-step-header">
                      <h3>{level.level}</h3>
                      <span className="shalya-education-step-label">{level.label}</span>
                    </div>
                    <span className="duration">{level.duration}</span>
                    <p className="education-focus">{level.focus}</p>
                    <div className="subjects-list">
                      {level.subjects.map((subject, idx) => (
                        <span key={idx} className="subject-tag">{subject}</span>
                      ))}
                    </div>
                  </div>
                </div>
                {index < educationPath.length - 1 && <div className="shalya-education-connector" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section section-alt">
        <div className="container">
          <div className="shalya-cta text-center">
            <h2>Begin Your Journey in Shalya Tantra</h2>
            <p>
              Explore the rich heritage of Ayurvedic surgery and discover how ancient wisdom meets modern medical
              practice.
            </p>
            <div className="cta-buttons">
              <Link to="/textbooks" className="btn btn-primary btn-large">
                <BookOpen size={20} />
                Start Learning
              </Link>
              <Link to="/contact" className="btn btn-secondary btn-large">
                <Users size={20} />
                Connect with Experts
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ShalyaTantra;
